import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Edit, Sparkles, RefreshCw, Check, X, Loader2, Rss, BellRing, Search, ChevronDown } from "lucide-react";
import { DatePicker } from "@/components/DatePicker";
import { ActorAvatar } from "@/components/ActorAvatar";
import { Seo } from "@/components/Seo";
import AdminTrending from "@/pages/AdminTrending";
import AdminTrivia from "./AdminTrivia";
import AdminReports from "@/pages/AdminReports";

const STATUSES = ["Now Playing", "Coming Soon", "Closing Soon", "Closed"];
const TABS = ["Stats", "Users", "Reports", "Shows", "Cast Events", "Suggestions", "Performers", "Trending", "News Feeds", "Trivia"];

const emptyShow = {
  name: "", venue: "", status: "Now Playing", opening_date: "", booking_until: "",
  next_performance: "", ticket_link: "", image_url: "", synopsis: "", is_sample: false,
  cast: [],
};

function Field({ label, ...props }) {
  return (
    <label className="block">
      <span className="text-sm font-medium">{label}</span>
      <input {...props} className="mt-1 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring" />
    </label>
  );
}

function ShowForm({ initial, onSaved, onCancel }) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const setCast = (i, k, v) => {
    const cast = [...form.cast];
    cast[i] = { ...cast[i], [k]: v };
    set("cast", cast);
  };

  const save = async () => {
    if (!form.name || !form.venue) return toast.error("Name and venue are required");
    setSaving(true);
    try {
      const payload = { ...form, cast: form.cast.filter((c) => c.actor_name?.trim()) };
      if (form.id) await api.put(`/shows/${form.id}`, payload);
      else await api.post("/shows", payload);
      toast.success("Show saved");
      onSaved();
    } catch (e) {
      toast.error("Failed to save show");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-6">
      <h3 className="font-serif text-xl font-semibold">{form.id ? "Edit show" : "Add a show"}</h3>
      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <Field label="Show name" data-testid="show-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} />
        <Field label="Venue" data-testid="show-venue-input" value={form.venue} onChange={(e) => set("venue", e.target.value)} />
        <label className="block">
          <span className="text-sm font-medium">Status</span>
          <select data-testid="show-status-select" value={form.status} onChange={(e) => set("status", e.target.value)} className="mt-1 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring">
            {STATUSES.map((s) => <option key={s}>{s}</option>)}
          </select>
        </label>
        <DatePicker label="Booking until (shown on cards)" testId="show-booking-input" value={form.booking_until || ""} onChange={(v) => set("booking_until", v)} />
        <DatePicker label="Opening date (Coming Soon shows switch to Now Playing on this date)" testId="show-opening-input" value={form.opening_date || ""} onChange={(v) => set("opening_date", v)} />
        <Field label="Next performance (optional)" type="datetime-local" value={form.next_performance || ""} onChange={(e) => set("next_performance", e.target.value)} />
        <Field label="Ticket link (URL)" data-testid="show-ticket-input" value={form.ticket_link || ""} onChange={(e) => set("ticket_link", e.target.value)} placeholder="https://…" />
        <Field label="Image URL (optional)" value={form.image_url || ""} onChange={(e) => set("image_url", e.target.value)} placeholder="https://…" />
      </div>
      <label className="mt-4 block">
        <span className="text-sm font-medium">Synopsis (optional)</span>
        <textarea value={form.synopsis || ""} onChange={(e) => set("synopsis", e.target.value)} rows={2} className="mt-1 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring" />
      </label>

      <label className="mt-4 flex items-center gap-2 text-sm">
        <input type="checkbox" data-testid="show-sample-checkbox" checked={form.is_sample} onChange={(e) => set("is_sample", e.target.checked)} />
        Mark as sample data (deletable in one action)
      </label>

      <div className="mt-5">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium">Principal cast</span>
          <button data-testid="add-cast-row" onClick={() => set("cast", [...form.cast, { actor_name: "", role: "", starts: "", ends: "" }])} className="inline-flex items-center gap-1 text-sm text-primary hover:underline">
            <Plus className="h-3.5 w-3.5" /> Add
          </button>
        </div>
        <div className="mt-2 space-y-2">
        {form.cast.map((c, i) => (
            <div key={i} className="flex flex-wrap gap-2">
              <input placeholder="Actor name" data-testid={`cast-actor-${i}`} value={c.actor_name} onChange={(e) => setCast(i, "actor_name", e.target.value)} className="flex-1 min-w-[140px] rounded-lg border border-input bg-background px-3 py-2 text-sm" />
              <input placeholder="Role" data-testid={`cast-role-${i}`} value={c.role} onChange={(e) => setCast(i, "role", e.target.value)} className="flex-1 min-w-[140px] rounded-lg border border-input bg-background px-3 py-2 text-sm" />
              <input type="date" placeholder="Starts" value={c.starts || ""} onChange={(e) => setCast(i, "starts", e.target.value)} className="w-36 rounded-lg border border-input bg-background px-3 py-2 text-sm" />
              <input type="date" placeholder="Ends (if known)" value={c.ends || ""} onChange={(e) => setCast(i, "ends", e.target.value)} className="w-36 rounded-lg border border-input bg-background px-3 py-2 text-sm" />
              <button onClick={() => set("cast", form.cast.filter((_, j) => j !== i))} className="rounded-lg p-2 text-destructive hover:bg-destructive/10"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-6 flex gap-3">
        <button data-testid="save-show-button" onClick={save} disabled={saving} className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground hover:bg-[#B82332] disabled:opacity-60">
          {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save show
        </button>
        <button onClick={onCancel} className="rounded-full border border-border px-5 py-2.5 text-sm font-medium hover:bg-secondary">Cancel</button>
      </div>
    </div>
  );
}

function ShowsTab() {
  const [shows, setShows] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => api.get("/shows").then((r) => setShows(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    if (!window.confirm("Delete this show?")) return;
    await api.delete(`/shows/${id}`);
    toast.success("Show deleted");
    load();
  };
  const delSamples = async () => {
    const { data } = await api.delete("/admin/sample-data");
    toast.success(`Deleted ${data.deleted} sample show(s)`);
    load();
  };
  const [sending, setSending] = useState(false);
  const sendReminders = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/admin/send-closing-reminders");
      toast.success(data.message);
    } catch { toast.error("Failed to send reminders"); }
    finally { setSending(false); }
  };

  const [closing, setClosing] = useState(false);
  const closeExpired = async () => {
    setClosing(true);
    try {
      const { data } = await api.post("/admin/close-expired-shows");
      if (data.closed_count === 0) toast.success("No shows needed closing");
      else toast.success(`Closed ${data.closed_count} show(s): ${data.closed_names.join(", ")}`);
      load();
    } catch { toast.error("Failed to close expired shows"); }
    finally { setClosing(false); }
  };

  const [checkingCast, setCheckingCast] = useState(false);
  const checkExpiredCast = async () => {
    setCheckingCast(true);
    try {
      const { data } = await api.post("/admin/flag-expired-cast");
      if (data.flagged_count === 0) {
        toast.success("No cast entries past their end date");
      } else {
        const lines = data.flagged.map((f) => `${f.actor_name} as ${f.role} in ${f.show_name} (ended ${f.ends})`);
        toast.error(`${data.flagged_count} to review: ${lines.join(" · ")}`, { duration: 15000 });
      }
    } catch { toast.error("Failed to check cast entries"); }
    finally { setCheckingCast(false); }
  };

  return (
    <div className="space-y-5">
      {editing ? (
        <ShowForm initial={editing} onSaved={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />
      ) : (
        <div className="flex flex-wrap gap-3">
          <button data-testid="new-show-button" onClick={() => setEditing(emptyShow)} className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground hover:bg-[#B82332]">
            <Plus className="h-4 w-4" /> Add show
          </button>
          <button data-testid="send-reminders-button" onClick={sendReminders} disabled={sending} className="inline-flex items-center gap-2 rounded-full border border-[hsl(var(--gold))] px-5 py-2.5 text-sm font-semibold text-[hsl(var(--gold))] hover:bg-[hsl(var(--gold))]/10 disabled:opacity-60">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <BellRing className="h-4 w-4" />} Send closing-soon reminders
          </button>
          <button data-testid="check-cast-button" onClick={checkExpiredCast} disabled={checkingCast} className="inline-flex items-center gap-2 rounded-full border border-border px-5 py-2.5 text-sm font-semibold hover:bg-secondary disabled:opacity-60">
            {checkingCast ? <Loader2 className="h-4 w-4 animate-spin" /> : <BellRing className="h-4 w-4" />} Check for expired cast entries
          </button>
          {shows.some((s) => s.is_sample) && (
            <button data-testid="delete-samples-button" onClick={delSamples} className="inline-flex items-center gap-2 rounded-full border border-destructive px-5 py-2.5 text-sm font-semibold text-destructive hover:bg-destructive/10">
              <Trash2 className="h-4 w-4" /> Delete all sample data
            </button>
          )}
        </div>
      )}

      <div className="space-y-2">
        {shows.map((s) => (
          <div key={s.id} data-testid={`admin-show-${s.id}`} className="flex items-center justify-between rounded-xl border border-border bg-card p-4">
            <div>
              <p className="font-serif font-semibold">{s.name} {s.is_sample && <span className="ml-1 rounded bg-amber-400 px-1.5 text-[10px] font-bold uppercase text-black">Sample</span>}</p>
              <p className="text-sm text-muted-foreground">{s.venue} · {s.status}</p>
            </div>
            <div className="flex gap-1">
              <button data-testid={`edit-show-${s.id}`} onClick={() => setEditing({ ...emptyShow, ...s })} className="rounded-lg p-2 hover:bg-secondary"><Edit className="h-4 w-4" /></button>
              <button data-testid={`delete-show-${s.id}`} onClick={() => del(s.id)} className="rounded-lg p-2 text-destructive hover:bg-destructive/10"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
        {shows.length === 0 && <p className="text-muted-foreground">No shows yet. Add your first one above.</p>}
      </div>
    </div>
  );
}

function CastEventsTab() {
  const [shows, setShows] = useState([]);
  const [form, setForm] = useState({ show_id: "", description: "", actor_name: "", role: "", change_type: "joins", change_date: "", note: "", source_link: "", status: "Approved" });
  const [events, setEvents] = useState([]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const load = useCallback(() => {
    api.get("/shows").then((r) => setShows(r.data));
    api.get("/cast-events").then((r) => setEvents(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.show_id || !form.description) return toast.error("Pick a show and describe the change");
    try {
      const { data } = await api.post("/cast-events", form);
      const update = data.cast_update;
      if (form.status !== "Approved") toast.success("Saved as suggestion");
      else if (update && update.applied) toast.success(`Logged & followers notified. ${update.message}`, { duration: 8000 });
      else if (update) toast.info(`Logged & followers notified. ${update.message}`, { duration: 10000 });
      else toast.success("Logged & followers notified");
      setForm({ show_id: "", description: "", actor_name: "", role: "", change_type: "joins", change_date: "", note: "", source_link: "", status: "Approved" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not log this change", { duration: 10000 });
    }
  };

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-border bg-card p-6">
        <h3 className="font-serif text-xl font-semibold">Log a cast change</h3>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="text-sm font-medium">Show</span>
            <select data-testid="event-show-select" value={form.show_id} onChange={(e) => set("show_id", e.target.value)} className="mt-1 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm">
              <option value="">Select…</option>
              {shows.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </label>
          <Field label="Actor name (optional)" value={form.actor_name} onChange={(e) => set("actor_name", e.target.value)} />
          <Field label="Role (optional; if set, updates Current Cast)" value={form.role} onChange={(e) => set("role", e.target.value)} />
          <label className="block">
            <span className="text-sm font-medium">Change type</span>
            <select value={form.change_type} onChange={(e) => set("change_type", e.target.value)} className="mt-1 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm">
              <option value="joins">Joins</option>
              <option value="leaves">Leaves</option>
              <option value="">Neither (just a note)</option>
            </select>
          </label>
          <DatePicker label="Change date" testId="event-date-input" value={form.change_date} onChange={(v) => set("change_date", v)} />
          <Field label="Source link (optional)" value={form.source_link} onChange={(e) => set("source_link", e.target.value)} placeholder="https://…" />
        </div>
        <label className="mt-4 block">
          <span className="text-sm font-medium">What changed</span>
          <input data-testid="event-description-input" value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="e.g. Actor X joins as Elphaba" className="mt-1 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm" />
        </label>
        <label className="mt-4 block">
          <span className="text-sm font-medium">Note (optional)</span>
          <textarea value={form.note} onChange={(e) => set("note", e.target.value)} rows={2} className="mt-1 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm" />
        </label>
        <button data-testid="log-event-button" onClick={submit} className="mt-4 inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground hover:bg-[#B82332]">
          <Plus className="h-4 w-4" /> Log & notify followers
        </button>
      </div>

      <div>
        <h3 className="font-serif text-xl font-semibold">Recent events</h3>
        <div className="mt-3 space-y-2">
          {events.filter((e) => e.status === "Approved").map((e) => (
            <div key={e.id} className="flex items-center justify-between rounded-xl border border-border bg-card p-4">
              <div><p className="font-medium">{e.description}</p><p className="text-sm text-muted-foreground">{e.show_name} · {e.change_date}</p></div>
              <button onClick={() => del(e.id)} className="rounded-lg p-2 text-destructive hover:bg-destructive/10"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function SuggestionRow({ e, onApprove, onReject }) {
  const [role, setRole] = useState(e.role || "");
  const [changeType, setChangeType] = useState(e.change_type || "");
  const [saving, setSaving] = useState(false);

  const approve = async () => {
    setSaving(true);
    try {
      if (role !== (e.role || "") || changeType !== (e.change_type || "")) {
        await api.put(`/cast-events/${e.id}`, { role: role || null, change_type: changeType || null });
      }
      await onApprove(e.id);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid={`suggestion-${e.id}`} className="rounded-xl border border-border bg-card p-4">
      <p className="font-medium">{e.description}</p>
      <p className="text-sm text-muted-foreground">{e.show_name}{e.actor_name ? ` · ${e.actor_name}` : ""}</p>
      {e.note && <p className="mt-1 text-xs text-muted-foreground">{e.note}</p>}

      <div className="mt-3 flex flex-wrap gap-2">
        <input
          value={role}
          onChange={(ev) => setRole(ev.target.value)}
          placeholder="Role (optional; lets approval update the cast)"
          className="flex-1 min-w-[180px] rounded-lg border border-input bg-background px-3 py-1.5 text-sm"
        />
        <select value={changeType} onChange={(ev) => setChangeType(ev.target.value)} className="rounded-lg border border-input bg-background px-3 py-1.5 text-sm">
          <option value="">No cast update</option>
          <option value="joins">Joins</option>
          <option value="leaves">Leaves</option>
        </select>
      </div>
      {changeType === "joins" && !role.trim() && (
        <p className="mt-2 text-xs text-muted-foreground">
          No role set, so followers will get the news email but the cast list won't change.
        </p>
      )}

      <div className="mt-3 flex gap-2">
        <button onClick={approve} disabled={saving} className="inline-flex items-center gap-1 rounded-full bg-primary px-4 py-1.5 text-sm font-semibold text-primary-foreground hover:bg-[#B82332] disabled:opacity-60">
          <Check className="h-4 w-4" /> Approve
        </button>
        <button onClick={() => onReject(e.id)} className="inline-flex items-center gap-1 rounded-full border border-border px-4 py-1.5 text-sm font-medium hover:bg-secondary">
          <X className="h-4 w-4" /> Reject
        </button>
        {e.source_link && <a href={e.source_link} target="_blank" rel="noopener noreferrer" className="ml-auto self-center text-sm text-primary hover:underline">Source</a>}
      </div>
    </div>
  );
}

function SuggestionsTab() {
  const [items, setItems] = useState([]);
  const [running, setRunning] = useState(false);
  const load = useCallback(() => api.get("/cast-events?status=Suggested").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const runAI = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/admin/ai-suggest");
      toast.success(data.message || `Drafted ${data.created}`);
      load();
    } catch { toast.error("AI suggestion failed"); }
    finally { setRunning(false); }
  };
  const [checking, setChecking] = useState(false);
  const checkAll = async () => {
    setChecking(true);
    try {
      const news = await api.post("/news/refresh");
      const ai = await api.post("/admin/ai-suggest");
      toast.success(`${news.data.added} new article(s). ${ai.data.message || `Drafted ${ai.data.created}`}`);
      load();
    } catch { toast.error("The cast change check failed"); }
    finally { setChecking(false); }
  };
  const approve = async (id) => {
    try {
      const { data } = await api.post(`/cast-events/${id}/approve`);
      const update = data.cast_update;
      if (update && update.applied) {
        toast.success(`Approved & followers notified. ${update.message}`, { duration: 8000 });
      } else if (update) {
        toast.info(`Approved & followers notified. ${update.message}`, { duration: 10000 });
      } else {
        toast.success("Approved & followers notified");
      }
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not approve this suggestion", { duration: 10000 });
    }
  };
  const del = async (id) => { await api.delete(`/cast-events/${id}`); load(); };
  const reject = async (id) => { await api.delete(`/cast-events/${id}`); toast.success("Suggestion rejected"); load(); };

  return (
    <div className="space-y-5">
      <div className="rounded-2xl border border-[hsl(var(--gold))]/40 bg-card p-6">
        <div className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-[hsl(var(--gold))]" /><h3 className="font-serif text-xl font-semibold">AI Suggestion Engine</h3></div>
        <p className="mt-2 text-sm text-muted-foreground">
          Scans recent news that mentions a show in your database and drafts a cast-change suggestion.
          Nothing is published or alerted until you approve it below.
        </p>
        <div className="mt-4 flex flex-wrap gap-3">
          <button data-testid="check-changes-button" onClick={checkAll} disabled={checking || running} className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground hover:bg-[#B82332] disabled:opacity-60">
            {checking ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Check for cast changes
          </button>
          <button data-testid="run-ai-button" onClick={runAI} disabled={running || checking} className="inline-flex items-center gap-2 rounded-full bg-[hsl(var(--gold))] px-5 py-2.5 text-sm font-semibold text-black hover:opacity-90 disabled:opacity-60">
            {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Draft suggestions from news
          </button>
        </div>
      </div>

      <div className="space-y-2">
        {items.length === 0 && <p className="text-muted-foreground">No pending suggestions. Run the engine above.</p>}
        {items.map((e) => (
          <SuggestionRow key={e.id} e={e} onApprove={approve} onReject={reject} />
        ))}
      </div>
    </div>
  );
}

function PerformersTab() {
  const [actors, setActors] = useState([]);
  const [q, setQ] = useState("");
  const [drafts, setDrafts] = useState({});
  const load = useCallback(() => api.get("/actors").then((r) => setActors(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const save = async (a) => {
    try {
      await api.put(`/actors/${a.id}`, { photo_url: drafts[a.id] ?? a.photo_url ?? "" });
      toast.success(`Photo saved for ${a.name}`);
      setDrafts((d) => { const n = { ...d }; delete n[a.id]; return n; });
      load();
    } catch { toast.error("Could not save photo"); }
  };

  const term = q.trim().toLowerCase();
  const list = actors.filter((a) => !term || a.name.toLowerCase().includes(term));
  const withPhoto = actors.filter((a) => a.photo_url).length;

  return (
    <div className="rounded-2xl border border-border bg-card p-6">
      <h3 className="font-serif text-xl font-semibold">Performer photos</h3>
      <p className="mt-1 text-sm text-muted-foreground">{withPhoto} of {actors.length} performers have a headshot. Paste an image URL to add or replace one.</p>
      <input data-testid="performer-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter performers…" className="mt-4 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm sm:w-72" />
      <div className="mt-4 max-h-[60vh] space-y-2 overflow-y-auto pr-1">
        {list.map((a) => (
          <div key={a.id} data-testid={`performer-row-${a.id}`} className="flex flex-col gap-2 rounded-lg border border-border p-3 sm:flex-row sm:items-center">
            <ActorAvatar name={a.name} photoUrl={drafts[a.id] ?? a.photo_url} size={40} />
            <span className="w-48 shrink-0 text-sm font-semibold">{a.name}</span>
            <input data-testid={`performer-photo-input-${a.id}`} value={drafts[a.id] ?? a.photo_url ?? ""} onChange={(e) => setDrafts((d) => ({ ...d, [a.id]: e.target.value }))} placeholder="https://…/photo.jpg" className="flex-1 rounded-lg border border-input bg-background px-3 py-1.5 text-xs" />
            <button data-testid={`performer-photo-save-${a.id}`} onClick={() => save(a)} disabled={drafts[a.id] === undefined} className="rounded-full border border-border px-3 py-1.5 text-xs font-semibold hover:bg-secondary disabled:opacity-40">Save</button>
          </div>
        ))}
      </div>
    </div>
  );
}

function FeedsTab() {
  const [feeds, setFeeds] = useState([]);
  const [form, setForm] = useState({ name: "", url: "" });
  const [refreshing, setRefreshing] = useState(false);
  const load = useCallback(() => api.get("/news/feeds").then((r) => setFeeds(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.name || !form.url) return toast.error("Name and URL required");
    await api.post("/news/feeds", form);
    setForm({ name: "", url: "" });
    load();
  };
  const del = async (id) => { await api.delete(`/news/feeds/${id}`); load(); };
  const refresh = async () => {
    setRefreshing(true);
    try { const { data } = await api.post("/news/refresh"); toast.success(`Added ${data.added} new item(s)`); }
    catch { toast.error("Refresh failed"); }
    finally { setRefreshing(false); }
  };

  return (
    <div className="space-y-5">
      <div className="rounded-2xl border border-border bg-card p-6">
        <div className="flex items-center gap-2"><Rss className="h-5 w-5 text-primary" /><h3 className="font-serif text-xl font-semibold">RSS feed sources</h3></div>
        <div className="mt-4 flex flex-col gap-2 sm:flex-row">
          <input data-testid="feed-name-input" placeholder="Source name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-lg border border-input bg-background px-3 py-2 text-sm sm:w-48" />
          <input data-testid="feed-url-input" placeholder="https://…/feed" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} className="flex-1 rounded-lg border border-input bg-background px-3 py-2 text-sm" />
          <button data-testid="add-feed-button" onClick={add} className="inline-flex items-center justify-center gap-1 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-[#B82332]"><Plus className="h-4 w-4" /> Add</button>
        </div>
        <div className="mt-4 space-y-2">
          {feeds.map((f) => (
            <div key={f.id} className="flex items-center justify-between rounded-lg border border-border p-3 text-sm">
              <div><span className="font-semibold">{f.name}</span> <span className="text-muted-foreground">{f.url}</span></div>
              <button onClick={() => del(f.id)} className="rounded p-1.5 text-destructive hover:bg-destructive/10"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
          {feeds.length === 0 && <p className="text-sm text-muted-foreground">No feeds yet. Add your trusted sources' RSS URLs above.</p>}
        </div>
        <button data-testid="refresh-news-button" onClick={refresh} disabled={refreshing || feeds.length === 0} className="mt-4 inline-flex items-center gap-2 rounded-full border border-border px-5 py-2.5 text-sm font-semibold hover:bg-secondary disabled:opacity-50">
          {refreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Refresh news now
        </button>
      </div>
    </div>
  );
}

function StatsTab() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => setStats(false));
  }, []);

  if (stats === null) return <p className="text-muted-foreground">Loading…</p>;
  if (stats === false) return <p className="text-destructive">Failed to load stats.</p>;

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-3">
        <div className="rounded-2xl border border-border bg-card p-5 text-center">
          <p className="font-serif text-3xl font-semibold">{stats.total_users}</p>
          <p className="mt-1 text-sm text-muted-foreground">Registered users</p>
        </div>
        <div className="rounded-2xl border border-border bg-card p-5 text-center">
          <p className="font-serif text-3xl font-semibold">{stats.total_show_follows}</p>
          <p className="mt-1 text-sm text-muted-foreground">Total show follows</p>
        </div>
        <div className="rounded-2xl border border-border bg-card p-5 text-center">
          <p className="font-serif text-3xl font-semibold">{stats.total_actor_follows}</p>
          <p className="mt-1 text-sm text-muted-foreground">Total performer follows</p>
        </div>
      </div>

      <div className="grid gap-6 sm:grid-cols-2">
        <div>
          <h3 className="font-serif text-xl font-semibold">Most followed shows</h3>
          <div className="mt-3 space-y-2">
            {stats.top_shows.length === 0 && <p className="text-sm text-muted-foreground">No shows followed yet.</p>}
            {stats.top_shows.map((s, i) => (
              <div key={s.show_id} className="flex items-center justify-between rounded-lg border border-border bg-card px-4 py-2.5">
                <span className="text-sm font-medium">{i + 1}. {s.name}</span>
                <span className="text-sm text-muted-foreground">{s.followers} follower{s.followers !== 1 ? "s" : ""}</span>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h3 className="font-serif text-xl font-semibold">Most followed performers</h3>
          <div className="mt-3 space-y-2">
            {stats.top_actors.length === 0 && <p className="text-sm text-muted-foreground">No performers followed yet.</p>}
            {stats.top_actors.map((a, i) => (
              <div key={a.name} className="flex items-center justify-between rounded-lg border border-border bg-card px-4 py-2.5">
                <span className="text-sm font-medium">{i + 1}. {a.name}</span>
                <span className="text-sm text-muted-foreground">{a.followers} follower{a.followers !== 1 ? "s" : ""}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function UsersTab() {
  const [users, setUsers] = useState(null);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);

  useEffect(() => {
    api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => setUsers(false));
  }, []);

  if (users === null) return <p className="text-muted-foreground">Loading…</p>;
  if (users === false) return <p className="text-destructive">Failed to load users.</p>;

  const term = q.trim().toLowerCase();
  const list = users.filter(
    (u) => !term || (u.email || "").toLowerCase().includes(term) || (u.name || "").toLowerCase().includes(term)
  );
  const joined = (iso) =>
    iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "unknown";
  const Row = ({ label, items }) => (
    <div className="mt-2 text-sm">
      <span className="font-medium">{label}: </span>
      <span className="text-muted-foreground">{items.length ? items.join(", ") : "none"}</span>
    </div>
  );

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        {users.length} account{users.length === 1 ? "" : "s"}, newest first. This is personal data: use it only to run the site, and do not share it.
      </p>
      <div className="relative max-w-sm">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by name or email…"
          className="w-full rounded-full border border-input bg-card py-2.5 pl-10 pr-4 text-sm outline-none focus:ring-2 focus:ring-ring"
        />
      </div>

      <div className="space-y-2">
        {list.length === 0 && <p className="text-muted-foreground">No accounts match.</p>}
        {list.map((u) => {
          const isOpen = open === u.id;
          return (
            <div key={u.id} className="rounded-xl border border-border bg-card">
              <button
                type="button"
                onClick={() => setOpen(isOpen ? null : u.id)}
                className="flex w-full items-center justify-between gap-4 p-4 text-left"
              >
                <div className="min-w-0">
                  <p className="truncate font-medium">
                    {u.name || "(no name)"}
                    {u.role === "admin" && (
                      <span className="ml-2 rounded bg-[hsl(var(--gold))]/20 px-1.5 py-0.5 text-[10px] font-bold uppercase text-[hsl(var(--gold))]">Admin</span>
                    )}
                  </p>
                  <p className="truncate text-sm text-muted-foreground">{u.email}</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Joined {joined(u.created_at)} · follows {u.followed_shows.length} show{u.followed_shows.length === 1 ? "" : "s"}
                    {" and "}{u.followed_actors.length} performer{u.followed_actors.length === 1 ? "" : "s"}
                  </p>
                </div>
                <ChevronDown className={`h-4 w-4 flex-shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`} />
              </button>
              {isOpen && (
                <div className="border-t border-border px-4 pb-4 pt-2">
                  <Row label="Follows shows" items={u.followed_shows} />
                  <Row label="Follows performers" items={u.followed_actors} />
                  <Row label="Want to see" items={u.want_shows} />
                  <Row label="Seen" items={u.seen_shows} />
                  <div className="mt-2 text-sm">
                    <span className="font-medium">Email alerts: </span>
                    <span className="text-muted-foreground">{u.email_alerts_off ? "off" : "on"}</span>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function Admin() {
  const [tab, setTab] = useState("Stats");
  const [openReports, setOpenReports] = useState(0);
  useEffect(() => {
    api.get("/admin/reports")
      .then((r) => setOpenReports((r.data.reports || []).filter((g) => !g.handled).length))
      .catch(() => {});
  }, []);
  return (
    <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6">
      <Seo title="Admin" noindex />
      <p className="overline-label text-[hsl(var(--gold))]">Owner only</p>
      <h1 className="mt-2 font-serif text-4xl font-semibold">Admin Panel</h1>

      <div className="mt-6 flex flex-wrap gap-2 border-b border-border">
        {TABS.map((t) => (
          <button
            key={t}
            data-testid={`admin-tab-${t.toLowerCase().replace(/ /g, "-")}`}
            onClick={() => setTab(t)}
            className={`-mb-px border-b-2 px-4 py-2.5 text-sm font-medium transition-colors ${
              tab === t ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {t}
            {t === "Reports" && openReports > 0 && (
              <span data-testid="admin-reports-count" className="ml-2 rounded-full bg-primary px-2 py-0.5 text-[11px] font-bold text-primary-foreground">{openReports}</span>
            )}
          </button>
        ))}
      </div>

      <div className="mt-6">
        {tab === "Stats" && <StatsTab />}
        {tab === "Users" && <UsersTab />}
        {tab === "Reports" && <AdminReports embedded onCount={setOpenReports} />}
        {tab === "Shows" && <ShowsTab />}
        {tab === "Cast Events" && <CastEventsTab />}
        {tab === "Suggestions" && <SuggestionsTab />}
        {tab === "Performers" && <PerformersTab />}
        {tab === "Trending" && <AdminTrending embedded />}
        {tab === "News Feeds" && <FeedsTab />}
        {tab === "Trivia" && <AdminTrivia />}
      </div>
    </div>
  );
}